// client/src/services/LeagueService.js
import BaseService from './BaseService'
import ApiDivisions from '../api/ApiDivisions'
import ApiLeagues from '../api/ApiLeagues'
import { calculateDivisionStandings } from '../utils/standings'
import { MATCH_STATUS, COMPLETED_STATUSES } from '../utils/matchStatus'

/**
 * Service for managing league and division operations
 */
class LeagueService extends BaseService {
  /**
   * Get division information for the current user in a league
   * @param {number} leagueId - ID of the league
   * @returns {Promise<Object>} Service response object with division data
   */
  async getMyDivision(leagueId) {
    return this.execute(
      async () => {
        this.validateParams({ leagueId }, ['leagueId'])

        const divInfo = await ApiDivisions.getMyDivision(leagueId)
        return divInfo
      },
      'Get my division',
      { dedupeKey: `division-${leagueId}` }, // Dedupe by league ID
    )
  }

  /**
   * Get all divisions for the current user (batched)
   * @returns {Promise<Object>} Service response object with all divisions data
   */
  async getMyDivisions() {
    return this.execute(
      async () => {
        const divisions = await ApiDivisions.getMyDivisions()
        return divisions || []
      },
      'Get my divisions',
      false,
    )
  }

  /**
   * Get leagues for the current user
   * @returns {Promise<Object>} Service response object with leagues data
   */
  async getMyLeagues() {
    return this.execute(
      async () => {
        const leagues = await ApiLeagues.getMyLeagues()
        return leagues || []
      },
      'Get my leagues',
      // Auto-dedupe enabled - getMyLeagues has no parameters so the description/context string is a stable dedupe key
    )
  }

  /**
   * Get unplayed opponents in a league
   * @param {number} leagueId - ID of the league
   * @returns {Promise<Object>} Service response object with opponents data
   */
  async getUnplayedOpponents(leagueId) {
    return this.execute(
      async () => {
        this.validateParams({ leagueId }, ['leagueId'])

        const response = await ApiLeagues.getUnplayedOpponents(leagueId)
        return response.opponents || []
      },
      'Get unplayed opponents',
      { dedupeKey: `unplayed-opponents-${leagueId}` }, // Dedupe by league ID
    )
  }

  /**
   * Get active leagues (those that are currently playable)
   * @returns {Promise<Object>} Service response object with active leagues
   */
  async getActiveLeagues() {
    return this.execute(
      async () => {
        const leaguesResponse = await this.getMyLeagues()

        if (!leaguesResponse.success) {
          throw new Error('Failed to get leagues')
        }

        const leagues = leaguesResponse.data || []
        const activeLeagues = leagues.filter((league) =>
          ['active', 'in_progress', 'playoffs'].includes(league.league_status),
        )

        return activeLeagues
      },
      'Get active leagues',
      false,
    )
  }

  /**
   * Get league statistics and progress
   * @param {number} leagueId - ID of the league
   * @returns {Promise<Object>} Service response object with league stats
   */
  async getLeagueStats(leagueId) {
    return this.execute(
      async () => {
        const divisionResponse = await this.getMyDivision(leagueId)
        const opponentsResponse = await this.getUnplayedOpponents(leagueId)

        if (!divisionResponse.success || !opponentsResponse.success) {
          throw new Error('Failed to get league statistics')
        }

        const division = divisionResponse.data
        const unplayedOpponents = opponentsResponse.data || []
        const matches = division.matches || []

        const matchStats = {
          total: matches.length,
          scheduled: matches.filter((m) => m.status === MATCH_STATUS.SCHEDULED)
            .length,
          completed: matches.filter((m) => COMPLETED_STATUSES.has(m.status))
            .length,
          unscheduled: unplayedOpponents.length,
        }

        return {
          leagueName: division.league_name,
          divisionName: division.division_name,
          playerId: division.player_id,
          matchStats,
          unplayedOpponents: unplayedOpponents.length,
          // Calculate progress percentage
          progressPercentage:
            matchStats.total > 0
              ? Math.round((matchStats.completed / matchStats.total) * 100)
              : 0,
        }
      },
      'Get league statistics',
      false,
    )
  }

  /**
   * Get division standings/position information
   * @param {number} leagueId - ID of the league
   * @returns {Promise<Object>} Service response object with standings data
   */
  async getDivisionStandings(leagueId) {
    return this.execute(
      async () => {
        const divisionResponse = await this.getMyDivision(leagueId)

        if (!divisionResponse.success) {
          throw new Error('Failed to get division information')
        }

        const division = divisionResponse.data

        const standings = calculateDivisionStandings(division)

        const myRank = standings.findIndex(
          (s) => s.player.id === division.player_id,
        )

        const myStats = standings[myRank] || {
          wins: 0,
          losses: 0,
          matchesPlayed: 0,
          winPercentage: 0,
        }

        return {
          wins: myStats.wins,
          losses: myStats.losses,
          matchesPlayed: myStats.matchesPlayed,
          winPercentage: parseFloat(myStats.winPercentage) || 0,
          position: myRank >= 0 ? myRank + 1 : 0,
          totalPlayers: standings.length,
        }
      },
      'Get division standings',
      false,
    )
  }

  /**
   * Get league-wide standings data
   * @param {number} leagueId - ID of the league
   * @returns {Promise<Object>} Service response object with league standings data
   */
  async getLeagueStandings(leagueId, leagueLevelId = null) {
    return this.execute(
      async () => {
        this.validateParams({ leagueId }, ['leagueId'])

        // Use the new dedicated league standings API endpoint
        const response = await ApiLeagues.getLeagueStandings(
          leagueId,
          leagueLevelId,
        )

        if (!response.success) {
          throw new Error('Failed to load league standings data')
        }

        const data = response.data

        // Load match scores for completed matches if any exist
        const completedMatches = data.allMatches.filter((m) =>
          COMPLETED_STATUSES.has(m.status),
        )

        if (completedMatches.length > 0) {
          try {
            const matchIds = completedMatches.map((match) => match.id)
            const scoresResponse = (await this.bulkGetMatchScores)
              ? await this.bulkGetMatchScores(matchIds)
              : await Promise.all(
                  matchIds.map(async (id) => {
                    // Fallback to individual requests if bulk method doesn't exist
                    try {
                      const ApiMatches = (await import('../api/ApiMatches'))
                        .default
                      const scoreData = await ApiMatches.getScores(id)
                      return { [id]: { scores: scoreData.scores || [] } }
                    } catch {
                      // Error loading scores, return empty
                      return { [id]: { scores: [] } }
                    }
                  }),
                )

            // Merge scores into matches
            if (Array.isArray(scoresResponse)) {
              // Handle array response from Promise.all
              const scoresMap = scoresResponse.reduce(
                (acc, item) => ({ ...acc, ...item }),
                {},
              )
              data.allMatches = data.allMatches.map((match) => {
                if (scoresMap[match.id]) {
                  return {
                    ...match,
                    set_scores: scoresMap[match.id].scores || [],
                  }
                }
                return match
              })
            } else if (scoresResponse.success && scoresResponse.data) {
              // Handle service response format
              data.allMatches = data.allMatches.map((match) => {
                if (
                  scoresResponse.data.scores &&
                  scoresResponse.data.scores[match.id]
                ) {
                  return {
                    ...match,
                    set_scores:
                      scoresResponse.data.scores[match.id].scores || [],
                  }
                }
                return match
              })
            }
          } catch (err) {
            console.warn('Could not load match scores:', err)
          }
        }

        // Preserve segmented league metadata so standings views can render
        // per-level tabs while still normalizing the fields this service owns.
        return {
          ...data,
          allPlayers: data.allPlayers,
          allMatches: data.allMatches,
          divisions: data.divisions,
          active_levels: data.active_levels || [],
          currentPlayerId: data.currentPlayerId,
          leagueLevelId: data.league_level_id || leagueLevelId,
          matchFormat: data.matchFormat || 'TRADITIONAL',
          playoffMinMatchesRequired: data.playoffMinMatchesRequired,
          leagueInfo: {
            id: data.leagueId,
            name: `League #${data.leagueId}`,
          },
        }
      },
      'Get league standings',
      false,
    )
  }
}

// Export singleton instance
const leagueService = new LeagueService()
export default leagueService
